import { useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { apiFetch } from "@/lib/api";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { useHubToast } from "@/components/ui/HubToast";

type AccessSystem = {
  id: number;
  name: string;
  kind: "app" | "login" | "other";
  isStandard: boolean;
  active: boolean;
  sortOrder: number;
  notes: string | null;
};

export function PeopleAppsPage() {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const writesAllowed = useHubModuleWrite("people");
  const readOnlyHint = "Your role has read-only access to the People module.";
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<AccessSystem["kind"]>("app");
  const [isStandard, setIsStandard] = useState(true);
  const [notes, setNotes] = useState("");

  const systemsQuery = useQuery({
    queryKey: ["people", "access-systems"],
    queryFn: () => apiFetch<{ systems: AccessSystem[] }>("/api/people/access-systems"),
  });

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ["people", "access-systems"] });
  }

  const createMutation = useMutation({
    mutationFn: () =>
      apiFetch("/api/people/access-systems", {
        method: "POST",
        body: { name, kind, isStandard, notes },
      }),
    onSuccess: async () => {
      setOpen(false);
      setName("");
      setNotes("");
      setIsStandard(true);
      await refresh();
    },
    onError: (error: Error) => showToast({ message: error.message, tone: "error" }),
  });

  const patchMutation = useMutation({
    mutationFn: (input: { id: number; body: Record<string, unknown> }) =>
      apiFetch(`/api/people/access-systems/${input.id}`, {
        method: "PATCH",
        body: input.body,
      }),
    onSuccess: refresh,
    onError: (error: Error) => showToast({ message: error.message, tone: "error" }),
  });

  const systems = systemsQuery.data?.systems ?? [];

  const columns: HubDataTableColumn<AccessSystem>[] = [
    {
      id: "name",
      header: "Name",
      sortValue: (row) => row.name,
      cell: (row) => row.name,
    },
    {
      id: "kind",
      header: "Kind",
      overflow: "nowrap",
      sortValue: (row) => row.kind,
      cell: (row) => row.kind,
    },
    {
      id: "isStandard",
      header: "Standard",
      overflow: "nowrap",
      sortValue: (row) => (row.isStandard ? 1 : 0),
      cell: (row) => (
        <input
          type="checkbox"
          checked={row.isStandard}
          disabled={!writesAllowed || patchMutation.isPending}
          aria-label={`${row.name} standard`}
          onChange={(event) =>
            patchMutation.mutate({ id: row.id, body: { isStandard: event.target.checked } })
          }
        />
      ),
    },
    {
      id: "active",
      header: "Active",
      overflow: "nowrap",
      sortValue: (row) => (row.active ? 1 : 0),
      cell: (row) => (
        <input
          type="checkbox"
          checked={row.active}
          disabled={!writesAllowed || patchMutation.isPending}
          aria-label={`${row.name} active`}
          onChange={(event) =>
            patchMutation.mutate({ id: row.id, body: { active: event.target.checked } })
          }
        />
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint="The standard list is the default set offered when you onboard someone. People already granted an app keep that grant when you add, rename, or retire a catalog row. Retire an app instead of deleting it so old checklists stay readable.">
          Apps
        </PageTitleWithHelp>
        <ReadOnlyHoverLayer active={!writesAllowed} hoverHint={readOnlyHint}>
          <Button type="button" disabled={!writesAllowed} onClick={() => setOpen(true)}>
            Add app
          </Button>
        </ReadOnlyHoverLayer>
      </div>

      <HubDataTable
        title="App catalog"
        columns={columns}
        rows={systems}
        rowKey={(row) => row.id}
        loading={systemsQuery.isLoading}
        error={systemsQuery.isError ? (systemsQuery.error as Error).message : null}
        empty="No apps."
      />

      {open
        ? createPortal(
            <div {...hubModalOverlayProps()} role="dialog" aria-modal="true" aria-labelledby="people-add-app-title">
              <div className="w-full max-w-lg rounded-lg bg-white p-4 shadow-lg">
                <h2 id="people-add-app-title" className="text-lg font-semibold text-slate-900">
                  Add app
                </h2>
            <div className="mt-3 space-y-3">
              <div>
                <Label htmlFor="people-app-name">Name</Label>
                <Input id="people-app-name" className="mt-1" value={name} onChange={(event) => setName(event.target.value)} />
              </div>
              <div>
                <Label htmlFor="people-app-kind">Kind</Label>
                <select
                  id="people-app-kind"
                  className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-2 text-sm"
                  value={kind}
                  onChange={(event) => setKind(event.target.value as AccessSystem["kind"])}
                >
                  <option value="app">App</option>
                  <option value="login">Login</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={isStandard} onChange={(event) => setIsStandard(event.target.checked)} />
                Include in the standard onboarding list
              </label>
              <div>
                <Label htmlFor="people-app-notes">Notes</Label>
                <Input id="people-app-notes" className="mt-1" value={notes} onChange={(event) => setNotes(event.target.value)} />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="button" disabled={name.trim() === "" || createMutation.isPending} onClick={() => createMutation.mutate()}>
                Add app
              </Button>
            </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
